import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { getPriorityLabel } from '../lib/priority';

/**
 * Two-step picker: choose source equipment, then choose one of its work orders.
 * Returns the selected WO via `onSelect(workOrder)` so the parent can prefill its form.
 */
const CopyWorkOrderDialog = ({ open, onClose, onSelect, excludeMachineryId }) => {
  const [machineryList, setMachineryList] = useState([]);
  const [locationsMap, setLocationsMap] = useState({});
  const [selectedMachineryId, setSelectedMachineryId] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingMachinery, setLoadingMachinery] = useState(false);
  const [loadingWOs, setLoadingWOs] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedMachineryId('');
      setWorkOrders([]);
      return;
    }
    setLoadingMachinery(true);
    Promise.all([
      axios.get(`${API}/machinery`),
      axios.get(`${API}/locations`),
    ])
      .then(([mRes, lRes]) => {
        const sorted = [...mRes.data].sort((a, b) =>
          (a.component || '').localeCompare(b.component || '')
        );
        setMachineryList(sorted);
        const lookup = {};
        lRes.data.forEach((l) => { lookup[l.id] = l.name; });
        setLocationsMap(lookup);
      })
      .catch(() => toast.error('Failed to load equipment list'))
      .finally(() => setLoadingMachinery(false));
  }, [open]);

  useEffect(() => {
    if (!selectedMachineryId) {
      setWorkOrders([]);
      return;
    }
    setLoadingWOs(true);
    axios
      .get(`${API}/work-orders/machinery/${selectedMachineryId}`)
      .then((res) => setWorkOrders(res.data))
      .catch(() => toast.error('Failed to load work orders for that equipment'))
      .finally(() => setLoadingWOs(false));
  }, [selectedMachineryId]);

  const handlePick = (wo) => {
    onSelect(wo);
    onClose();
  };

  const filteredMachinery = excludeMachineryId
    ? machineryList.filter((m) => m.id !== excludeMachineryId)
    : machineryList;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="copy-work-order-dialog"
      >
        <DialogHeader>
          <DialogTitle>Copy from another work order</DialogTitle>
          <DialogDescription>
            Pick the source equipment, then choose one of its work orders. Title, description,
            priority, frequency, reminder, and notes will prefill into the new work order.
            Due date and assigned tech stay blank; status resets to Not Started.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="copy-source-machinery">Source equipment</Label>
            <select
              id="copy-source-machinery"
              data-testid="copy-source-machinery-select"
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              value={selectedMachineryId}
              onChange={(e) => setSelectedMachineryId(e.target.value)}
              disabled={loadingMachinery}
            >
              <option value="">
                {loadingMachinery ? 'Loading equipment…' : 'Select equipment…'}
              </option>
              {filteredMachinery.map((m) => {
                const locName = m.plant_location_id
                  ? locationsMap[m.plant_location_id] || 'Unknown plant'
                  : 'Unassigned';
                return (
                  <option key={m.id} value={m.id}>
                    {m.component} — {locName}
                  </option>
                );
              })}
            </select>
          </div>

          {selectedMachineryId && (
            <div className="space-y-2">
              <Label>Work orders on this equipment</Label>
              {loadingWOs ? (
                <div className="text-sm text-slate-600 py-4">Loading…</div>
              ) : workOrders.length === 0 ? (
                <div className="text-sm text-slate-600 border rounded-md p-4 text-center">
                  No work orders found on this equipment.
                </div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {workOrders.map((wo) => (
                    <button
                      key={wo.id}
                      type="button"
                      data-testid={`copy-source-wo-${wo.id}`}
                      onClick={() => handlePick(wo)}
                      className="w-full text-left border rounded-md p-3 hover:bg-slate-50 transition"
                    >
                      <div className="font-medium flex items-center gap-2">
                        {wo.wo_number && (
                          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            #{wo.wo_number}
                          </span>
                        )}
                        <span>{wo.title}</span>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {wo.priority && <span>Priority: {getPriorityLabel(wo.priority)}</span>}
                        {wo.frequency_days && (
                          <span>Recurs every {wo.frequency_days} days</span>
                        )}
                        {wo.reminder_enabled && <span>Reminder on</span>}
                        <span>Status: {wo.status.replace('_', ' ')}</span>
                      </div>
                      {wo.description && (
                        <div className="text-sm text-slate-700 mt-2 line-clamp-2">
                          {wo.description}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose} data-testid="copy-wo-cancel-btn">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyWorkOrderDialog;
